/**
 * Utilitário de pluralização para PT-BR.
 *
 * Elimina o padrão "item(s)" / "item(is)" substituindo-o por
 * concordância gramatical correta em tempo de execução.
 *
 * Regra PT-BR: singular = 1; plural = tudo o mais (0, 2, 3...).
 */

/**
 * Retorna a forma singular ou plural conforme count.
 *
 * @param {number} count
 * @param {string} singular - forma singular: 'local', 'serviço', 'ausência'
 * @param {string} [plural] - forma plural opcional; se omitida, usa `singular + 's'`
 * @returns {string} a palavra na forma correta (sem o número)
 *
 * @example
 * pluralize(1, 'local', 'locais')   // 'local'
 * pluralize(2, 'local', 'locais')   // 'locais'
 * pluralize(0, 'local', 'locais')   // 'locais'  (0 é plural em PT-BR)
 * pluralize(1, 'agenda')            // 'agenda'
 * pluralize(3, 'agenda')            // 'agendas' (fallback: +s)
 */
export function pluralize(count, singular, plural) {
  return count === 1 ? singular : (plural || `${singular}s`);
}

/**
 * Conveniência: retorna "N palavra" com forma correta.
 *
 * @param {number} count
 * @param {string} singular - forma singular
 * @param {string} [plural] - forma plural opcional
 * @returns {string} ex: '1 profissional', '3 profissionais'
 *
 * @example
 * pluralizeWithCount(1, 'profissional', 'profissionais')  // '1 profissional'
 * pluralizeWithCount(3, 'profissional', 'profissionais')  // '3 profissionais'
 * pluralizeWithCount(0, 'agenda')                         // '0 agendas'
 */
export function pluralizeWithCount(count, singular, plural) {
  return `${count} ${pluralize(count, singular, plural)}`;
}

/**
 * Concorda o particípio passado com gênero e número.
 *
 * @param {number} count
 * @param {string} masc  - forma masculina: 'criado', 'selecionado', 'cadastrado'
 * @param {string} fem   - forma feminina:  'criada', 'selecionada', 'cadastrada'
 * @param {'m'|'f'} [gender='m'] - gênero do substantivo ao qual o particípio se refere
 * @returns {string} ex: 'selecionado', 'selecionados', 'selecionada', 'selecionadas'
 *
 * @example
 * pluralizePastParticiple(1, 'selecionado', 'selecionada', 'm')  // 'selecionado'
 * pluralizePastParticiple(2, 'selecionado', 'selecionada', 'm')  // 'selecionados'
 * pluralizePastParticiple(1, 'cadastrado',  'cadastrada',  'f')  // 'cadastrada'
 * pluralizePastParticiple(3, 'cadastrado',  'cadastrada',  'f')  // 'cadastradas'
 */
export function pluralizePastParticiple(count, masc, fem, gender = 'm') {
  const base = gender === 'f' ? fem : masc;
  return count === 1 ? base : `${base}s`;
}
